// Registry reads for `ref="instruction://…"` (RFC-0025 §3): one anonymous
// `resources/read` over MCP Streamable HTTP using plain fetch — no SDK in the
// embed bundle, no credentials from the page, public read path only.
//
// On MCP's 2026-07-28 wire, statelessly: no `initialize` and no session, so the
// read is one request, which any replica of a clustered server answers (the
// owner, 2026-10-24: "the latest protocol version that does not require session
// id"). The request carries the protocol version, the client and its
// capabilities in `_meta` (SEP-2575), and the headers mirror the body (SEP-2243).
import type { RenderMeta } from "./project";

export interface RegistryRead { text: string; meta: RenderMeta; raw: Record<string, unknown> }

const PROTOCOL = "2026-07-28";
const CLIENT = { name: "instruction-md-render", version: "0.1.0" };

/** SEP-2243's header-value form: plain printable ASCII as it is, else `=?base64?…?=` of its UTF-8 bytes. */
function headerValue(v: string): string {
  if (/^[\x21-\x7e](?:[\x20-\x7e]*[\x21-\x7e])?$/.test(v) && !v.startsWith("=?base64?")) return v;
  let bin = "";
  for (const b of new TextEncoder().encode(v)) bin += String.fromCharCode(b);
  return `=?base64?${btoa(bin)}?=`;
}

export function metaFromRead(m: Record<string, unknown>): RenderMeta {
  const s = (k: string) => { const v = m[`md.instruction/${k}`]; return typeof v === "string" ? v : undefined; };
  const keyState = s("signatureKeyState");
  const revoked = m["md.instruction/revoked"] as { at: string; reason: string } | undefined;
  return {
    canonical: s("canonical"), versionId: s("versionId"), revision: typeof m["md.instruction/revision"] === "number" ? (m["md.instruction/revision"] as number) : undefined,
    ref: s("ref"), tag: s("tag"), publisher: s("publisher"), kid: s("kid"), digest: s("digest"),
    signature: s("signature") ? (keyState === "revoked" ? "revoked-key" : "signed") : "unsigned",
    revoked: revoked ?? null,
  };
}

/** Read one instruction URI anonymously: one stateless `resources/read`. */
export async function readInstruction(registryUrl: string, uri: string, opts: { maxBytes?: number; timeoutMs?: number } = {}): Promise<RegistryRead> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), opts.timeoutMs ?? 15_000);
  try {
    const body = { jsonrpc: "2.0", id: 1, method: "resources/read", params: { uri, _meta: { "io.modelcontextprotocol/protocolVersion": PROTOCOL, "io.modelcontextprotocol/clientInfo": CLIENT, "io.modelcontextprotocol/clientCapabilities": {} } } };
    const res = await fetch(registryUrl, {
      method: "POST", body: JSON.stringify(body), signal: ctl.signal, credentials: "omit",
      headers: { "content-type": "application/json", accept: "application/json, text/event-stream", "mcp-protocol-version": PROTOCOL, "mcp-method": "resources/read", "mcp-name": headerValue(uri) },
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`registry ${res.status}: ${text.slice(0, 200)}`);
    let msg: { id?: unknown; result?: { contents?: { text?: string; _meta?: Record<string, unknown> }[] }; error?: { message: string } } | null = null;
    if ((res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      // (the answer is the event carrying this request's id; anything before it is a notification)
      for (const line of text.split("\n")) if (line.startsWith("data:")) { try { const m = JSON.parse(line.slice(5).trim()); if (m?.id === 1) msg = m; } catch { /* keep looking */ } }
    } else msg = text ? JSON.parse(text) : null;
    if (msg?.error) throw new Error(msg.error.message);
    const c = msg?.result?.contents?.[0];
    if (!c || typeof c.text !== "string") throw new Error("the registry returned no content");
    const cap = opts.maxBytes ?? 1_048_576;
    if (c.text.length > cap) throw new Error(`document exceeds max-bytes (${cap})`);
    const raw = c._meta ?? {};
    return { text: c.text, meta: metaFromRead(raw), raw };
  } finally { clearTimeout(timer); }
}
